<script lang="ts">
export const cardCls = cva("card", {
	variants: {
		variant: {
			default: "shadow-sm",
			elevated: "shadow-sm",
			outlined: "border-border border",
		},
		content: {
			false: null,
			true: "card-content",
		},
		color: {
			default: "",
			neutral: "bg-neutral text-neutral-content",
			primary: "bg-primary text-primary-content",
			secondary: "bg-secondary text-secondary-content",
			accent: "bg-accent text-accent-content",
			info: "bg-info text-info-content",
			success: "bg-success text-success-content",
			warning: "bg-warning text-warning-content",
			error: "bg-error text-error-content",
		},
	},
	defaultVariants: {
		variant: "outlined",
	},
});

type Variants = VariantProps<typeof cardCls>;

export interface CardProps extends BasePrimitiveProps, DeprecatedCustomSizeProps {
	variant?: Variants["variant"];
	color?: Variants["color"];

	/** Apply padding, gap and text-size on the card body */
	content?: Variants["content"] & boolean;

	/** @deprecated **/
	outlined?: boolean;
}

interface Props extends CardProps, /* @vue-ignore */ Omit<HTMLAttributes, keyof CardProps> {}
</script>

<script setup lang="ts">
import { cva, type VariantProps } from "class-variance-authority";
import { Primitive, useForwardExpose } from "reka-ui";
import { computed, useAttrs, warn, type HTMLAttributes } from "vue";
import type { BasePrimitiveProps } from "../../lib/base-props";
import { deprecatedSizeToCSS, type DeprecatedCustomSizeProps } from "../../lib/size";
import { cn, htmlAttrs } from "../../lib/utils";

defineOptions({ inheritAttrs: false });

const { as = "div", ...props } = defineProps<Props>() as CardProps;
const attrs = useAttrs() as HTMLAttributes;

const style = computed(() => {
	const style = deprecatedSizeToCSS(props);
	if (Object.keys(style).length > 0) {
		warn(
			"[UI] Using deprecated size props for Card. Please use tailwindcss utility class instead."
		);
	}

	return style;
});

const variant = computed<(typeof props)["variant"]>(() => {
	if (props.outlined) {
		return "outlined";
	}

	return props.variant;
});

const { forwardRef } = useForwardExpose();
</script>

<template>
	<Primitive
		:ref="forwardRef"
		:as="as"
		:asChild="props.asChild"
		:class="cn(cardCls({ ...props, variant }))"
		:style="style"
		v-bind="htmlAttrs(attrs)"
	>
		<slot />
	</Primitive>
</template>
